import T from 'prop-types' // eslint-disable-line
import { toJsStyle, toPythonStyle } from '@xhs/data-transform'
import { commonChecks } from '@xhs/ozone-detector'
import {
  invoke, OzoneCalendarStatus, addMeasure,
} from '@xhs/ozone-schema'

import { adapter, callbackAdapter } from './libs/bridgeAdapter'
import { resultType, urlType } from './libs/validators'
import { appVersionLt, appVersionGte } from './libs/version'
import isSupport from './libs/isSupport'

import BridgeError from './utils/BridgeError'
import { warnDeprecated } from './utils/logDeprecated'
import { checkArgs, checkRes } from './utils/checks'
import completeUrl, { webp2png } from './utils/completeUrl'

// iOS 平台短震动需要传入的一个固定值
const iOSShortVibrateSourceID = 1519

/**
 * Deprecated
 */
export function isApp() {
  warnDeprecated('isApp', 'commonChecks.isXHS in @xhs/ozone-detector')

  return commonChecks.isXHS
}

/**
 * 获取 App 信息
 */

/**
 * 登录后置
 */
export function checkLoginWithAction(data) {
  const spec = {
    argsT: T.shape({
      type: T.number.isRequired,
    }),
    resT: T.shape({
      result: resultType,
    }),
  }

  return adapter('checkLoginWithAction', data, spec)
}

export function logout() {
  return adapter('logout')
}

export function isAppInstalled(data) {
  const spec = {
    argsT: T.shape({
      iOS: T.string,
      Android: T.string,
    }),
    resT: T.shape({
      result: resultType,
      value: T.bool.isRequired,
    }),
  }
  if (commonChecks.isIOS) {
    return adapter('isAppInstalled', data, spec)
  }
  return invoke('isAppInstalled', data).catch(() => adapter('isAppInstalled', data, spec))
}

export function getAppInfo() {
  const spec = {
    resT: T.shape({
      result: resultType,
      version: T.string.isRequired,
      build: T.string.isRequired,
      jsversion: T.string.isRequired,
      package: T.oneOf(['com.xingin.discover', 'com.xingin.xhs']).isRequired,
    }),
  }

  return adapter('getAppInfo', spec)
}

// ios的deviceId值对应安卓的uniqueId,最终需要消费的也是uniqueId的值
function adaptGetDeviceInfo(data) {
  if (commonChecks.isIOS) {
    data.deviceId = data.uniqueId
  }
  return data
}

function getDeviceInfoOld() {
  function imeiType() {
    if (appVersionLt('5.24')) {
      return {}
    }

    return commonChecks.isIOS
      ? { idfa: T.string.isRequired, idfv: T.string.isRequired }
      : { imei: T.string.isRequired }
  }

  const spec = {
    resT: T.shape({
      result: resultType,
      appMarket: T.string.isRequired,
      appVersion: T.string.isRequired,
      buildNumber: T.string.isRequired,
      systemVersion: T.string.isRequired,
      deviceModel: T.string.isRequired,
      manufacturer: T.string.isRequired,
      timezone: T.string.isRequired,
      deviceScreenWidth: T.number.isRequired,
      deviceScreenHeight: T.number.isRequired,
      carrier: T.string.isRequired,
      uniqueId: T.string.isRequired,
      ...(commonChecks.isIOS ? { applepayInfo: T.bool.isRequired } : {}),
      ...imeiType,
    }),
  }

  return adapter('getDeviceInfo').then(res => {
    const newRes = toJsStyle(res)

    if (res.applepay_info) {
      newRes.applepay_info = res.applepay_info
      warnDeprecated('res.applepay_info', 'res.applepayInfo')
    }

    checkRes(newRes, spec.resT)
    return newRes
  })
}

export async function getDeviceInfo() {
  try {
    const res = await invoke('getDeviceInfo')
    const { result, value } = res || {}
    if (result === 0) {
      return adaptGetDeviceInfo(value)
    }
    return getDeviceInfoOld()
  } catch (error) {
    return getDeviceInfoOld()
  }
}

function getNetworkTypeLegacy(config = { extractValue: false }) {
  const spec = {
    resT: T.shape({
      result: resultType,
      value: T.oneOf(['WiFi', '4G', '3G+', '3G', '2G', 'NONE']),
    }),
  }

  return adapter('getNetworkType').then(res => {
    const newRes = { ...res }

    // FIXME: 兼容 iOS 返回的 'wifi'；兼容 'WIFI'
    if (res.value === 'wifi' || res.value === 'WIFI') {
      newRes.value = 'WiFi'
    }

    checkRes(newRes, spec.resT)

    if (config.extractValue) {
      if (newRes.result === 0) {
        return newRes.value
      }

      throw new BridgeError(`ERR_RESULT_${newRes.result}`, 'res.result is not 0')
    }
    return newRes
  })
}

export async function getNetworkType(config = { extractValue: false }) {
  try {
    const res = await invoke('getNetworkType')
    const { result, value } = res || {}
    if (result === 0) {
      return config.extractValue ? value : res
    }
  } catch (error) {
    return getNetworkTypeLegacy(config)
  }
  return getNetworkTypeLegacy(config)
}

export function getUserInfo(config = { extractValue: false }) {
  const startTime = Date.now()
  console.warn(
    '[OzoneBridge] getUserInfo was deprecated, use `getUserInfoV2` and `getABFlag` to replace, more info see readme',
  )
  const supportHashExp = (commonChecks.isAndroid && appVersionGte('6.5'))
    || (commonChecks.isIOS && appVersionGte('6.9'))

  const spec = {
    resT: T.shape({
      result: resultType,
      response: T.shape({
        userId: T.string.isRequired,
        nickname: T.string.isRequired,
        gender: T.number.isRequired,
        userToken: T.string.isRequired,
        image: urlType.isRequired,
        location: T.string.isRequired,
        flags: T.shape({
          fulishe: T.oneOfType([T.object]).isRequired,
          shequ: T.oneOfType([T.object]).isRequired,
        }),
        ...(appVersionGte('5.24')
          ? {
            lat: T.number.isRequired,
            lon: T.number.isRequired,
            sessionId: T.string.isRequired,
          }
          : {}),
        ...(supportHashExp
          ? {
            hashExp: T.string.isRequired,
          }
          : {}),
      }),
    }),
  }

  // 旧版getUserInfo新加统计
  return adapter('getUserInfo').then(res => {
    const newRes = toJsStyle(res)

    if (res.response) {
      // flags 不处理 underscoreCase，因为 flag 是实验的 key，由开发者自行设置，且其消费
      // 该值时一般是通过框架，将 flag 作为字符串传入即可，不做转换就不需要额外的认知负担
      if (res.response.flags) {
        if (res.response.flags.fulishe) {
          newRes.response.flags.fulishe = res.response.flags.fulishe
        }

        if (res.response.flags.shequ) {
          newRes.response.flags.shequ = res.response.flags.shequ
        }
      }

      if (res.response.user_id) {
        newRes.response.user_id = res.response.user_id
        warnDeprecated('res.response.user_id', 'res.response.userId')
      }

      if (res.response.user_token) {
        newRes.response.user_token = res.response.user_token
        warnDeprecated('res.response.user_token', 'res.response.userToken')
      }

      if (res.response.session_id) {
        newRes.response.session_id = res.response.session_id
        warnDeprecated('res.response.session_id', 'res.response.sessionId')
      }

      if (res.response.images && !res.response.image) {
        newRes.response.image = res.response.images
        warnDeprecated('res.response.images', 'res.response.image')
      }
    }

    webp2png(newRes)

    checkRes(newRes, spec.resT)

    if (config.extractValue) {
      if (newRes.result === 0) {
        addMeasure({
          platformType: 'H5',
          bridgeName: 'getUserInfoOld',
          timing: Date.now() - startTime,
        })
        return newRes.response
      }

      throw new BridgeError(`ERR_RESULT_${newRes.result}`, 'res.result is not 0')
    }
    addMeasure({
      platformType: 'H5',
      bridgeName: 'getUserInfoOld',
      timing: Date.now() - startTime,
    })
    return newRes
  })
}

export function getUserInfoV2() {
  return invoke('getUserInfo').then(res => {
    webp2png(res)
    return res
  }).catch(() => getUserInfo({ extractValue: true }).then(res => ({
    result: 0,
    message: 'success',
    value: res,
  })))
}

export async function getSession() {
  const res = await getUserInfoV2()
  if (res?.result === 0) {
    return res.value?.sessionId
  }
  throw new Error('Session 获取失败')
}

function getTrackEnvOld() {
  const spec = {
    resT: T.shape({
      isTestEnv: T.bool,
      uploadOneByOne: T.bool,
      sessionId: T.string,
    }),
  }

  return adapter('getTrackEnv').then(res => {
    const newRes = toJsStyle(res)

    if (res.session_id) {
      newRes.sessionId = res.session_id
    }

    checkRes(newRes, spec.resT)
    return newRes
  })
}

export async function getTrackEnv() {
  try {
    const res = await invoke('getTrackEnv')
    const { result, value } = res || {}
    if (result === 0) {
      value.session_id = value.sessionId
      return value
    }
  } catch (error) {
    return getTrackEnvOld()
  }
  return getTrackEnvOld()
}

export function lowPowerModeEnabled() {
  const spec = {
    resT: T.shape({
      result: resultType,
      value: T.bool.isRequired,
    }),
  }

  return adapter('lowPowerModeEnabled', spec)
}

export function requestNotificationPermission(data) {
  const spec = {
    argsT: T.shape({
      engaingType: T.number.isRequired,
      engaingMessage: T.string,
    }),
  }

  return adapter('requestNotificationPermission', data, spec)
}

export function saveImage(data) {
  const spec = {
    argsT: T.shape({
      url: urlType,
      base64string: T.string,
      type: T.string.isRequired,
    }),
    resT: T.shape({
      result: resultType,
    }),
  }

  return adapter('saveImage', data, spec)
}

export function basicSendClientRequest(config) {
  return adapter('sendClientRequest', config)
}

export function sendClientRequest(data) {
  console.warn(
    '[OzoneBridge] sendClientRequest was deprecated, please use [Launcher http](https://code.devops.xiaohongshu.com/formula/launcher/tree/master/src/http)',
  ) // eslint-disable-line no-console

  const spec = {
    argsT: T.shape({
      url: T.string,
      type: T.oneOf(['GET', 'POST', 'PUT', 'DELETE']).isRequired,
      data: T.oneOfType([T.object]),
      transform: T.oneOfType([T.bool, T.shape({ separateNumber: T.bool }), T.func]),
    }),
    resT: T.shape({
      result: resultType,
      response: T.oneOfType([T.object]),
      status: T.number,
    }),
  }

  checkArgs(data, spec.argsT, 'sendClientRequest')

  const cloneData = { ...data }
  const transformOpts = cloneData.transform

  if (transformOpts && typeof cloneData.data === 'object') {
    cloneData.data = toPythonStyle(cloneData.data, transformOpts)
    delete cloneData.transform
  }

  return basicSendClientRequest(cloneData)
    .then(res => {
      checkRes(res, spec.resT)

      if (transformOpts) {
        return toJsStyle(res, transformOpts)
      }

      return res
    })
    .then(resp => {
      // iOS的业务报错形式为{ result: -1, status: xxx }
      if (resp.result === -1) {
        const error = new Error()
        error.status = resp.status
        throw error
      }

      const { response } = resp

      // 安卓的业务错误会放在这里
      if (
        (typeof response.success === 'undefined' && response.result !== 0)
        || response.success === false
      ) {
        const error = new Error()
        error.status = response.result
        throw error
      }

      return response.data || response
    })
}

export function sendClientRequestV2(config) {
  const newConfig = {
    ...config,
    url: completeUrl(config.url),
  }

  // FIXME: iOS 处理了业务错误信息（response.body.result|success)，并且没有返回给我们，只能回退到 sendClientRequest
  if (isSupport('sendClientRequestV2') && commonChecks.isAndroid) {
    return adapter('sendClientRequestV2', newConfig)
  }

  return basicSendClientRequest(newConfig).then(res => {
    const { result, status, response } = res

    /**
     * Android 的 status 一般为状态码，为 -1 时表示网络错误
     */
    if (commonChecks.isAndroid) {
      if (result === -1) {
        if (status !== -1) {
          return {
            result: 0,
            response: {
              status,
            },
          }
        }

        return {
          result,
        }
      }

      return {
        result,
        response: {
          status: 200,
          body: response,
        },
      }
    }

    /**
     * iOS 的 res.status === res.response.status，没有使用价值
     */
    if (result === -1) {
      // result 返回了 -1，但是不能确定具体的状态码
      if (response) {
        return {
          result: 0,
          response: {
            status: 200,
            body: response,
          },
        }
      }

      return {
        result,
      }
    }

    return {
      result,
      response: {
        status: 200,
        body: response,
      },
    }
  })
}

export function sendClientRequestV3(config) {
  let headers = { ...config.headers }
  const requestMethod = config.method.toUpperCase()
  const { 'Content-Type': contentType, ...restHeaders } = headers
  /**
   * sendClientRequestV3 边界case:
   * 1、requestMethod 是 post, put, delete 的场景，需要有 Content-Type
   * 2、contentType 没有指定或者不是 'application/x-www-form-urlencoded' | 'application/json' 二者之一，则默认指定为 'application/json'
   */
  if (
    requestMethod !== 'GET'
    && !['application/x-www-form-urlencoded', 'application/json'].includes(contentType)
  ) {
    headers['Content-Type'] = 'application/json'
  } else if (requestMethod === 'GET' && contentType) {
    // iOS 平台 get 请求不能有 Content-Type
    headers = restHeaders
  }

  // headers 域需要前端转换为 string
  Object.keys(headers).forEach(key => {
    if (typeof headers[key] !== 'string') {
      headers[key] = String(headers[key])
    }
  })

  const newConfig = {
    ...config,
    url: completeUrl(config.url),
    headers,
  }

  return invoke('sendClientRequestV3', newConfig)
}

/**
 * 数据预传
 * https://xiaohongshu.quip.com/z0eCAN4QdMUN
 */
export function getPrevData() {
  const spec = {
    resT: T.oneOfType([T.array, T.bool, T.number, T.object, T.string]),
  }

  return adapter('getPrevData', spec)
}

/**
 * CStorage
 * https://xiaohongshu.quip.com/Dy4rAj17V3br
 */
function getItemOld(key) {
  const spec = {
    argsT: T.string.isRequired,
    resT: T.shape({
      result: resultType,
      value: T.string,
    }),
  }

  checkArgs(key, spec.argsT, 'getItem')

  return adapter('getItem', { key }, { resT: spec.resT })
}

export async function getItem(key) {
  try {
    const res = await invoke('getItem', { key })
    return res
  } catch (error) {
    return getItemOld(key)
  }
}

function setItemOld(key, value) {
  const spec = {
    argsT: T.shape({
      key: T.string.isRequired,
      value: T.string.isRequired,
    }),
    resT: T.shape({
      result: resultType,
    }),
  }

  return adapter('setItem', { key, value }, spec)
}

export async function setItem(key, value) {
  try {
    const res = await invoke('setItem', { key, value })
    return res
  } catch (error) {
    return setItemOld(key, value)
  }
}

export function removeItemOld(key) {
  const spec = {
    argsT: T.string.isRequired,
    resT: T.shape({
      result: resultType,
    }),
  }
  checkArgs(key, spec.argsT, 'removeItem')

  return adapter('removeItem', { key }, { resT: spec.resT })
}

export async function removeItem(key) {
  try {
    const res = await invoke('removeItem', { key })
    return res
  } catch (error) {
    return removeItemOld(key)
  }
}

/**
 * 用于多 Web 页面间的通信
 * 如：订单支付页切换地址
 * 1. B 页面 registerNotice
 * 2. A 页面 broadcast data 给 App
 * 3. App 将 data 发送给 B 页面
 */
// FIXME: registerNotice should be a event rather than a bridge
export function registerNoticeLegacy(callback) {
  return callbackAdapter('registerNotice', undefined, callback)
}

export function registerNotice(callback) {
  warnDeprecated('registerNotice', "subscribe('notice', () => {})")

  return registerNoticeLegacy(callback)
}

export function broadcast(data) {
  return adapter('broadcast', data)
}

export function broadcastNative(data) {
  const spec = {
    argsT: T.shape({
      key: T.string.isRequired,
      data: T.string.isRequired,
    }),
  }

  return adapter('broadcastNative', data, spec)
}

export function getMessageStatusIOS() {
  const spec = {
    resT: T.shape({
      result: resultType,
      status: T.oneOf([0, 1]).isRequired,
    }),
  }

  return adapter('getMessageStatus', spec)
}

export function getMessageStatus() {
  warnDeprecated('getMessageStatus', 'getMessageStatusIOS')

  return getMessageStatusIOS()
}

export function getThirdAuth(data) {
  const spec = {
    argsT: T.oneOf(['weixin']).isRequired,
    resT: T.shape({
      result: resultType,
      value: T.oneOfType([T.object]),
    }),
  }

  return adapter('getThirdAuth', data, spec)
}

export function getCurrentGeolocation() {
  return adapter('getCurrentGeolocation')
}

export function checkAppPermission(data) {
  const spec = {
    argsT: T.string.isRequired,
    resT: T.shape({
      result: T.oneOf([0, -1]).isRequired,
      state: T.oneOf(['denied', 'granted', 'undeterminated']),
    }),
  }
  return adapter('checkAppPermission', data, spec)
}

export function areNotificationsEnabledAndroid() {
  const spec = {
    resT: T.shape({
      result: T.oneOf([0, -1]).isRequired,
      state: T.oneOf(['denied', 'granted']),
    }),
  }
  return adapter('areNotificationsEnabled', spec)
}

export async function getFileUrlFromLocalServerIOS(data) {
  const spec = {
    argsT: T.shape({
      url: urlType.isRequired,
    }),
    resT: T.shape({
      result: T.oneOf([0, -1]).isRequired,
      value: urlType,
      message: T.string,
    }),
  }

  const res = await adapter('getFileUrlFromLocalServer', data, spec)

  if (res.result === 0) {
    return res.value
  }

  throw new BridgeError(`ERR_RESULT_${res.result}`, res.message)
}

export async function toggleLocalDns(enable) {
  const res = await adapter('toggleLocalDns', {
    enable,
  })

  if (res.result === 0) {
    return
  }

  throw new BridgeError(`ERR_RESULT_${res.result}`, res.message)
}

export async function shortVibrate() {
  if (commonChecks.isIOS) {
    // iOS 通过 bridge 实现短震动
    return invoke('shortVibrate', { id: iOSShortVibrateSourceID })
  }
  if (window?.navigator?.vibrate) {
    // 安卓平台用浏览器的实现
    window.navigator.vibrate(50)
    // 安卓的返回值和 iOS 兼容一致
    return Promise.resolve({
      message: 'success',
      result: 0,
    })
  }
  throw new BridgeError('ERR_NOT_SUPPORT', 'not support shortVibrate')
}

export async function findCalendarEvent(args) {
  try {
    const res = await invoke('authorizeCalendarStatus')
    const { value } = res || {}
    if (value === OzoneCalendarStatus.Authorized) {
      return invoke('findCalendarEvent', args)
    }
    throw new BridgeError(`calendar permission: ${value}`)
  } catch (error) {
    return Promise.reject(error)
  }
}

export async function removeCalendarEvent(args) {
  try {
    const res = await invoke('authorizeCalendarStatus')
    const { value } = res || {}
    if (value === OzoneCalendarStatus.Authorized) {
      return invoke('removeCalendarEvent', args)
    }
    throw new BridgeError(`calendar permission: ${value}`)
  } catch (error) {
    return Promise.reject(error)
  }
}

export async function saveCalendarEvent(args) {
  try {
    const res = await invoke('authorizeCalendarStatus')
    const { value } = res || {}
    if (value === OzoneCalendarStatus.Authorized) {
      return invoke('saveCalendarEvent', args)
    }
    throw new BridgeError(`calendar permission: ${value}`)
  } catch (error) {
    return Promise.reject(error)
  }
}

export async function registerTrickleConnectTopic(args) {
  if (commonChecks.isIOS || (commonChecks.isAndroid && appVersionGte('6.83'))) {
    return invoke('registerTrickleConnectTopic', args)
  }
  return adapter('registerTrickleConnectTopic', args)
}

export function getABFlag(args) {
  return invoke('getABFlag', args).catch(() => getUserInfo({ extractValue: true }).then(res => {
    const { key } = args
    const { flags } = res || {}
    const {
      fulishe, shequ, ios, android,
    } = flags || {}
    const platformAB = commonChecks.isIOS ? ios : android
    let value = null
    if (shequ?.[key]) {
      value = shequ[key]
    } else if (fulishe?.[key]) {
      value = fulishe[key]
    } else if (platformAB?.[key]) {
      value = platformAB[key]
    }
    return {
      result: 0,
      message: 'success',
      value,
    }
  }))
}
